// ---------------------------------------------------------------
// useUsers.js — Quién hay en la plantilla.
//
// Dos fuentes, a propósito:
//   · useUsers()            → `directorio`: nombre, rol y si sigue en
//                              la plantilla. Lo puede leer todo el
//                              equipo y es lo que usan casi todas las
//                              pantallas (turnos, tareas, intercambios).
//   · useFichasCompletas()  → `users`: la ficha entera, con correo,
//                              fecha de alta y última conexión. SÓLO
//                              la administración (las reglas lo
//                              impiden al resto).
//
// Antes todo el mundo leía `users`, así que el móvil de cada empleado
// descargaba el correo y la última conexión de todos sus compañeros,
// y esa copia se quedaba guardada en el dispositivo.
// ---------------------------------------------------------------
import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { sharedSubscribe } from "../utils/sharedSnapshot";

/** Escucha compartida de una colección de personas */
function usePersonas(coleccion, activo, ordenar) {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(activo);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activo) {
      setLista([]);
      setLoading(false);
      return undefined;
    }
    // Una sola escucha por colección para TODA la web
    return sharedSubscribe(
      coleccion,
      (onData, onErr) =>
        onSnapshot(
          collection(db, coleccion),
          (snapshot) => {
            const docs = snapshot.docs.map((d) => ({ uid: d.id, ...d.data() }));
            docs.sort(ordenar);
            onData(docs);
          },
          onErr
        ),
      (docs) => {
        setLista(docs);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(`Error leyendo ${coleccion}:`, err);
        setError(err);
        setLoading(false);
      }
    );
  }, [coleccion, activo, ordenar]);

  /* `users` es LA PLANTILLA: quien sigue en la farmacia. Quien está
     dado de baja no aparece en cuadrantes, tareas, intercambios ni
     listas de compañeros. Para las pantallas que necesitan el histórico
     completo (gestión de usuarios, horas del año) está `todos`.
     Sin el campo = activo: las fichas de siempre no lo tienen. */
  const activos = useMemo(() => lista.filter((u) => u.activo !== false), [lista]);
  return { users: activos, todos: lista, loading, error };
}

const porNombre = (a, b) =>
  String(a.username || a.displayName || "").localeCompare(
    String(b.username || b.displayName || ""),
    "es"
  );
const masRecientes = (a, b) =>
  (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);

/** La plantilla, sólo con lo que hace falta para trabajar juntos */
export function useUsers() {
  return usePersonas("directorio", true, porNombre);
}

/** Fichas completas (correo, alta, última conexión). Sólo administración. */
export function useFichasCompletas(activo = true) {
  return usePersonas("users", activo, masRecientes);
}
